import React from "react";
import { Container, Box, Typography, Button, Stack } from "@mui/material";

interface PromoApp {
  name: string;
  tagline: string;
  description: string;
  playStoreUrl: string;
  iconUrl: string;
  screenshots: string[];
  footnote: string;
  trailerId?: string;
}

// Self-hosted rather than pulled from Google's CDN at render time — the
// play-lh.googleusercontent.com domain rate-limits repeated/automated requests,
// which was silently breaking these images.
const PLAY_BADGE_URL = "/assets/routinex/play_badge.png";

const PROMO_APPS: PromoApp[] = [
  {
    name: "RoutineX",
    tagline: "AI Powered Habit Tracker",
    description:
      "Build lasting healthy habits with a built-in AI coach that works 100% offline — no account required, no data tracking, complete privacy.",
    playStoreUrl:
      "https://play.google.com/store/apps/details?id=np.com.shashisharma.habittracker",
    iconUrl: "/assets/routinex/icon.webp",
    screenshots: [
      "/assets/routinex/playstore1.webp",
      "/assets/routinex/playstore2.webp",
      "/assets/routinex/playstore3.webp",
      "/assets/routinex/playstore4.webp",
      "/assets/routinex/playstore5.webp",
    ],
    footnote: "Free · Rated 3+ · Works 100% offline",
    trailerId: "Cw0YK7_7LAY",
  },
  {
    name: "Piece It",
    tagline: "Jigsaw Puzzle",
    description:
      "Relaxing jigsaw puzzles with real interlocking pieces. Drag, snap, and solve beautiful images at your own pace. Solve, unwind, repeat!",
    playStoreUrl:
      "https://play.google.com/store/apps/details?id=np.com.shashisharma.pieceit",
    iconUrl: "/assets/pieceit/icon.webp",
    screenshots: [
      "/assets/pieceit/playstore1.webp",
      "/assets/pieceit/playstore2.webp",
      "/assets/pieceit/playstore3.webp",
      "/assets/pieceit/playstore4.webp",
    ],
    footnote: "Free · Relaxing puzzle game",
  },
];

const AppPromoBanner: React.FC<{ app: PromoApp }> = ({ app }) => {
  const trailerEmbedUrl = app.trailerId
    ? `https://www.youtube.com/embed/${app.trailerId}`
    : "";
  const youtubeWatchUrl = app.trailerId
    ? `https://www.youtube.com/watch?v=${app.trailerId}`
    : "";
  return (
    <Box
      sx={{
        py: { xs: 3, md: 4 },
        background:
          "linear-gradient(135deg, rgba(145, 189, 58, 0.12) 0%, rgba(145, 189, 58, 0.03) 45%, rgba(2, 132, 199, 0.05) 100%)",
        borderBottom: "1px solid",
        borderColor: "divider",
      }}
    >
      <Container maxWidth="lg">
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={{ xs: 3, md: 4 }}
          alignItems={{ xs: "center", md: "flex-start" }}
        >
          {/* App identity + download */}
          <Box sx={{ flex: 1, width: "100%", maxWidth: { md: 430 } }}>
            <Stack
              direction="row"
              spacing={1.5}
              alignItems="center"
              sx={{ mb: 1 }}
            >
              <Box
                component="img"
                src={app.iconUrl}
                alt={`${app.name} app icon`}
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: 2.5,
                  border: "1px solid",
                  borderColor: "divider",
                  boxShadow: "0 4px 12px rgba(15, 23, 42, 0.12)",
                  flexShrink: 0,
                }}
              />
              <Box>
                <Typography
                  variant="h5"
                  sx={{ fontWeight: 800, lineHeight: 1.15, mb: 0.25 }}
                >
                  {app.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {app.tagline}
                </Typography>
              </Box>
            </Stack>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 1.5, maxWidth: 400 }}
            >
              {app.description}
            </Typography>

            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1}
              alignItems={{ xs: "flex-start", sm: "center" }}
              sx={{ mb: 1 }}
            >
              <Box
                component="a"
                href={app.playStoreUrl}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  lineHeight: 0,
                  borderRadius: 1,
                  "&:hover": { boxShadow: 1 },
                }}
              >
                <Box
                  component="img"
                  src={PLAY_BADGE_URL}
                  alt="Get it on Google Play"
                  loading="lazy"
                  sx={{
                    width: 172,
                    height: "auto",
                    display: "block",
                  }}
                />
              </Box>
              {app.trailerId && (
                <Button
                  component="a"
                  href={youtubeWatchUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  color="error"
                  startIcon={
                    <Box
                      component="span"
                      sx={{
                        display: "inline-flex",
                        width: 10,
                        height: 13,
                        clipPath: "polygon(0 0, 100% 50%, 0 100%)",
                        backgroundColor: "currentColor",
                      }}
                    />
                  }
                  sx={{ textTransform: "none", fontWeight: 700, p: 0.75 }}
                >
                  Watch Trailer
                </Button>
              )}
            </Stack>

            <Typography variant="caption" color="text.secondary">
              {app.footnote}
            </Typography>
          </Box>

          {/* Trailer + screenshots */}
          <Box sx={{ flex: 1.1, width: "100%" }}>
            {app.trailerId && (
              <Box
                component="iframe"
                src={trailerEmbedUrl}
                title={`${app.name} trailer`}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                sx={{
                  width: "100%",
                  maxWidth: { md: 480 },
                  ml: { md: "auto" },
                  display: "block",
                  aspectRatio: "16 / 9",
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: "divider",
                  boxShadow: "0 8px 24px rgba(15, 23, 42, 0.12)",
                  backgroundColor: "#000",
                }}
              />
            )}

            <Stack
              direction="row"
              spacing={1.25}
              sx={{
                mt: 1.5,
                overflowX: "auto",
                pb: 1,
                scrollSnapType: "x proximity",
              }}
            >
              {app.screenshots.map((url, i) => (
                <Box
                  key={url}
                  component="img"
                  src={url}
                  alt={`${app.name} screenshot ${i + 1}`}
                  loading="lazy"
                  sx={{
                    width: 120,
                    aspectRatio: "400 / 717",
                    objectFit: "contain",
                    flexShrink: 0,
                    scrollSnapAlign: "start",
                    borderRadius: 1.5,
                    border: "1px solid",
                    borderColor: "divider",
                    backgroundColor: "background.default",
                  }}
                />
              ))}
            </Stack>
          </Box>
        </Stack>
      </Container>
    </Box>
  );
};

const AppPromo: React.FC = () => (
  <>
    {PROMO_APPS.map((app) => (
      <AppPromoBanner key={app.name} app={app} />
    ))}
  </>
);

export default AppPromo;
